import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, ComboboxList, ComboboxOption } from './combobox.js';

// #52: Das verbindliche Bedienmuster fuer alle Katalog-Tabellen (Spec
// docs/mindCoder/specs/2026-07-22-katalog-bedienmuster-design.md, Kapitel 2).
// Eine Zeile hat drei Zustaende: Ruhe, Bearbeiten, Loeschen-Rueckfrage. Anlegen ist kein
// eigener Zustand, sondern eine leere Zeile im Bearbeiten-Zustand — deshalb koennen Anlegen
// und Bearbeiten hier gar nicht auseinanderdriften.
//
// Der Entwurf (TDraft) ist ein eigener Typ und nicht die Zeile: beim Anlegen gibt es keine
// Zeile, und feste Felder (vergebene IDs, abgeleitete Werte) sollen gar nicht erst im Entwurf
// liegen. Die Umrechnung kennt nur der aufrufende Tab (toDraft), weil nur er die Feldnamen
// der API kennt.

export type CatalogEditor<TDraft> =
  | {
      kind: 'text' | 'textarea';
      get: (draft: TDraft) => string;
      set: (draft: TDraft, value: string) => TDraft;
      required?: boolean;
    }
  | {
      kind: 'checkbox';
      get: (draft: TDraft) => boolean;
      set: (draft: TDraft, value: boolean) => TDraft;
    }
  | {
      kind: 'select';
      get: (draft: TDraft) => string | null;
      set: (draft: TDraft, value: string | null) => TDraft;
      options: { value: string; label: string }[];
      /** Blenden Optional-Felder eine "Ohne"-Auswahl ein. Fehlt ⇒ keine Leeroption — sonst
       *  bietet ein Pflichtfeld (z. B. die Glossar-Quelle) einen Zustand an, der nie gilt
       *  (Finding aus Task-3-Review). */
      allowEmpty?: boolean;
      emptyLabel?: string;
    }
  | {
      // #47: Datenart-Auswahl mit Inline-Anlage. Anders als `select` ist die Optionsliste kein
      // abschliessender Satz erlaubter Werte — der Nutzer darf tippen, filtern, eine bestehende
      // Datenart waehlen ODER (bei canCreate) eine neue anlegen. Ausgeschrieben statt als
      // Freitext, damit eine getippte Tippgeahnlichkeit zu einer bestehenden Datenart nicht
      // stillschweigend zu einem Duplikat wird (`similar`).
      kind: 'combobox';
      /** Gewaehlte data_kind_id, falls eine bestehende Datenart ausgewaehlt ist. */
      get: (draft: TDraft) => string | null;
      /** Setzt eine Auswahl (oder loescht sie mit null) — muss selbst einen etwaigen
       *  `getNew`-Wert im Draft leeren, Auswahl und Inline-Anlage sind exklusiv. */
      set: (draft: TDraft, value: string | null) => TDraft;
      /** Traegt den Freitextnamen einer Inline-Anlage in den Draft ein. */
      setNew: (draft: TDraft, name: string) => TDraft;
      /** Freitextname einer laufenden Inline-Anlage, sonst null. */
      getNew: (draft: TDraft) => string | null;
      options: { value: string; label: string }[];
      /** Ohne canCreate: reine Auswahl, kein "+ anlegen". */
      canCreate: boolean;
      /** Client-seitige Tippgeahnlichkeits-Pruefung — kein Request, der Tab liefert sie synchron. */
      similar: (typed: string) => { value: string; label: string } | null;
    };

export type CatalogColumn<TRow, TDraft> = {
  /** Stabil — adressiert die Spalte in Fehlermeldungen (mapError.columnKey). */
  key: string;
  header: string;
  width?: string;
  /** Ruhezustand. */
  render: (row: TRow) => ReactNode;
  /** Fehlt ⇒ festes Feld: im Bearbeiten-Zustand als Text dargestellt, nie als Eingabe. */
  editor?: CatalogEditor<TDraft>;
};

// #81a: Ohne eigene `width` bekommt eine Spalte diese Mindestbreite fuer die Scroll-Berechnung —
// eine benannte Konstante statt eines Literals in computeTableMinWidth/JSX, damit sie an einer
// Stelle aenderbar bleibt. Bei 1280 Viewport-Breite quetschte `table-layout: fixed` sonst jede
// Spalte gleichmaessig zusammen, bis sich die Kopfzeilen ueberlagerten ("BESCHREIBUNGBESCHREIBUNG")
// und Zellinhalt buchstabenweise umbrach.
export const MIN_UNSIZED_COLUMN_WIDTH = '6rem';

// Breite der Aktionsspalte — muss zum `w-44` auf dem <th> weiter unten passen (Tailwinds
// Default-Spacing-Skala: 44 * 0.25rem = 11rem). Eigene Konstante, damit beide Stellen nicht
// auseinanderlaufen koennen.
export const ACTIONS_COLUMN_WIDTH = '11rem';

// I4 (Review-Finding): Hilfsfunktion fuer Tabs, die ihre Spaltenbreiten aus einem exportierten
// Array bauen (glossary-tab.tsx u. a. — Grund: computeTableMinWidth soll gegen ECHTE Breiten
// testbar sein, nicht gegen einen synthetischen Satz). `width?: string` mit
// `exactOptionalPropertyTypes: true` nimmt `undefined` als expliziten Wert NICHT an — die
// Property muss bei einer unbepreisten Spalte ganz FEHLEN, nicht auf `undefined` stehen. Ein
// bedingtes Spread-Objekt erreicht genau das.
export function columnWidth(w: string | undefined): { width?: string } {
  return w === undefined ? {} : { width: w };
}

/**
 * Mindestbreite der ganzen Tabelle: Summe aus den Spaltenbreiten (explizite `width` je Spalte,
 * unbepreiste Spalten ueber MIN_UNSIZED_COLUMN_WIDTH) plus die Aktionsspalte — via CSS `calc()`
 * addiert, nicht in Pixel umgerechnet. `calc()` darf Werte unterschiedlicher Einheiten (%, rem,
 * px) mischen; dadurch behalten Prozent-Spalten (Glossar, Testdaten) ihr bisheriges, flexibles
 * Verhalten (kein neuer Scrollbalken, wo vorher keiner war), waehrend unbepreiste Spalten einen
 * garantierten Platzbedarf durchsetzen. Erst DIESER garantierte Bedarf bringt den
 * Container-Scrollbalken (`overflow-x-auto`) ueberhaupt zum Anspringen, wenn zu viele Spalten
 * ohne Breite antreten — vorher passte sich `table-layout: fixed` klaglos an jede
 * Container-Breite an, bis nichts mehr lesbar war.
 */
export function computeTableMinWidth(columns: { width?: string }[]): string {
  // I3-Fix (Review-Finding): hat JEDE Spalte schon eine eigene Breite, besteht das #81a-Risiko
  // (unbepreiste Spalten quetschen sich unter table-fixed auf MIN_UNSIZED_COLUMN_WIDTH zusammen)
  // gar nicht — die Tabelle ist ueber ihre eigenen Spaltenbreiten bereits selbst-begrenzend, und
  // die Aktionsspalte hat ihre feste Breite laengst ueber die `w-44`-Klasse auf dem <th> (siehe
  // JSX unten), unabhaengig von diesem minWidth-Wert. Eine zusaetzliche additive
  // ACTIONS_COLUMN_WIDTH war hier reiner Overhead: beim E2E-Tab (18% + 30% + 30% + 10% = 88%,
  // vier durchgehend bepreiste Spalten) erzwang calc(88% + 176px) einen Scrollbalken fuer JEDE
  // Containerbreite unter ~1467px — genau den Scrollbalken, den Task 1 ausdruecklich NICHT wollte.
  // Nur wenn tatsaechlich eine unbepreiste Spalte im Spiel ist (Glossar/Testdaten/
  // Vereinfachungen: je eine Textspalte ohne eigene width), greift die urspruengliche
  // #81a-Absicherung inkl. Aktionsspalten-Zuschlag unveraendert weiter.
  const hasUnsizedColumn = columns.some((c) => c.width === undefined);
  const columnTerms = columns.map((c) => c.width ?? MIN_UNSIZED_COLUMN_WIDTH);
  if (!hasUnsizedColumn) {
    return `calc(${columnTerms.join(' + ')})`;
  }
  return `calc(${[...columnTerms, ACTIONS_COLUMN_WIDTH].join(' + ')})`;
}

type EditState<TDraft> = {
  /** null ⇒ Anlegen. */
  key: string | null;
  draft: TDraft;
  initial: TDraft;
  focusColumn: string | null;
  error: { columnKey: string; message: string } | null;
  saving: boolean;
};

type ConfirmState = {
  key: string;
  /** Bleibt gesetzt, solange onDelete laeuft — verhindert Doppel-Klicks. */
  deleting: boolean;
  /** Fehlertext einer fehlgeschlagenen Loeschung; die Zeile bleibt dafuer in der Rueckfrage. */
  error: string | null;
};

export type CatalogTableProps<TRow, TDraft> = {
  columns: CatalogColumn<TRow, TDraft>[];
  rows: TRow[];
  rowKey: (row: TRow) => string;
  toDraft: (row: TRow) => TDraft;
  emptyDraft: TDraft;
  onCreate: (draft: TDraft) => Promise<unknown>;
  onUpdate: (key: string, draft: TDraft) => Promise<unknown>;
  onDelete: (key: string) => Promise<unknown>;
  /** Benennt den Datensatz in der Loesch-Rueckfrage — „Kunde 100234 loeschen?". */
  deleteLabel: (row: TRow) => string;
  /** Uebersetzt einen Server-Fehler in Spalte + Meldung. Nur der Tab kennt seine Fehlercodes. */
  mapError?: (err: unknown) => { columnKey: string; message: string } | null;
  canEdit: boolean;
  addLabel: string;
  /** Nur E2E: geordnete Mitgliederliste unterhalb der Zeile. */
  expanded?: (row: TRow) => ReactNode;
  /** Zusatzaktionen je Zeile (z. B. Reihenfolge), links vor Bearbeiten/Loeschen. Nur im
   *  Ruhezustand gerendert: eine Aktion, die eine Query invalidiert, wuerde sonst den Draft
   *  einer offenen Bearbeiten-Zeile vernichten. */
  rowActions?: (row: TRow) => ReactNode;
  /** Spalte, die den Cursor bekommt, wenn KEINE bestimmte Zelle angeklickt wurde (Anlegen-Knopf,
   *  Bearbeiten-Knopf). Ohne Angabe die erste Spalte mit `editor` — was solange stimmt, wie die
   *  erste bedienbare Spalte auch die ist, in die man zuerst tippt. Der E2E-Tab setzt sie
   *  ausdruecklich: dort steht seit #112 die Stream-AUSWAHL vorn, und wer nach "+ Neu" den Namen
   *  tippt, loeste dort nur die Tastatursuche des <select> aus — sichtbar passierte nichts. */
  defaultFocusColumn?: string;
  /** 2. Abnahme, Befund 4: optionale Gruppenebene — Zwischenüberschrift mit Anzahl, zuklappbar.
   *  Fehlt die Funktion, verhält sich die Tabelle exakt wie bisher (flache Zeilenliste).
   *
   *  Warum in CatalogTable und nicht im E2E-Tab: A3 verlangt „Stream → E2E-Prozess" als SICHT,
   *  nicht als Spalte. Eine Gruppenebene nur im E2E-Tab wäre das fünfte Bedienkonzept in einer
   *  Tabellenfamilie, die #52 gerade auf EINS zusammengeführt hat — und die Prozess-Sicht wird
   *  dieselbe Ebene früher oder später ebenso brauchen.
   *
   *  Die Reihenfolge der Gruppen ergibt sich aus der Reihenfolge der Zeilen (erstes Auftreten):
   *  der Tab sortiert bereits, die Tabelle sortiert nicht ein zweites Mal.
   */
  groupBy?: (row: TRow) => { key: string; label: string };
};

export function CatalogTable<TRow, TDraft>({
  columns,
  rows,
  rowKey,
  toDraft,
  emptyDraft,
  onCreate,
  onUpdate,
  onDelete,
  deleteLabel,
  mapError,
  canEdit,
  addLabel,
  expanded,
  rowActions,
  defaultFocusColumn,
  groupBy,
}: CatalogTableProps<TRow, TDraft>) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<EditState<TDraft> | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Zugeklappte Gruppen. Vorgabe: alle AUFgeklappt — die Gruppierung soll die Sicht zeigen, nicht
  // sie verstecken. (Anders als die Testplan-Auswahl, wo zehn Gruppen mit je 50 Testfällen stehen;
  // hier ist eine Gruppe eine Handvoll Ketten.)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  const dirty = edit !== null && JSON.stringify(edit.draft) !== JSON.stringify(edit.initial);

  // Siehe `defaultFocusColumn`: eine Stelle fuer beide Einstiege ohne Zellbezug.
  const fallbackFocusKey =
    (defaultFocusColumn !== undefined &&
    columns.some((c) => c.key === defaultFocusColumn && c.editor)
      ? defaultFocusColumn
      : columns.find((c) => c.editor)?.key) ?? null;

  const openEdit = (row: TRow, columnKey: string | null) => {
    if (!canEdit) return;
    // Eine ungespeicherte Zeile gibt den Platz nicht frei — sonst verschwindet die Eingabe
    // beim Klick daneben, ohne dass jemand es gewollt hat.
    if (dirty) return;
    const draft = toDraft(row);
    setConfirm(null);
    setEdit({
      key: rowKey(row),
      draft,
      initial: draft,
      focusColumn: columnKey,
      error: null,
      saving: false,
    });
  };

  const openCreate = () => {
    if (!canEdit || dirty) return;
    const first = fallbackFocusKey;
    setConfirm(null);
    setEdit({
      key: null,
      draft: emptyDraft,
      initial: emptyDraft,
      focusColumn: first,
      error: null,
      saving: false,
    });
  };

  const cancel = () => setEdit(null);

  const save = async () => {
    if (!edit || edit.saving) return;
    setEdit({ ...edit, saving: true, error: null });
    try {
      if (edit.key === null) await onCreate(edit.draft);
      else await onUpdate(edit.key, edit.draft);
      setEdit(null);
    } catch (err) {
      // Die Zeile bleibt offen und die Eingabe stehen — sonst tippt der Nutzer alles neu.
      const mapped = mapError?.(err) ?? null;
      setEdit((cur) =>
        cur
          ? {
              ...cur,
              saving: false,
              error: mapped ?? {
                columnKey: columns.find((c) => c.editor)?.key ?? '',
                message: t('catalog.errors.generic'),
              },
            }
          : cur,
      );
    }
  };

  const setDraft = (next: TDraft) => setEdit((cur) => (cur ? { ...cur, draft: next } : cur));

  // Eine Zeile, egal ob gruppiert oder nicht — genau EINE Stelle, an der eine Zeile entsteht.
  // Zwei Kopien (eine je Zweig) wären die Sorte Doppelung, aus der später zwei Verhaltensweisen
  // werden.
  const renderRow = (row: TRow) => {
    const key = rowKey(row);
    const editing = edit?.key === key ? edit : null;
    const confirming = confirm?.key === key ? confirm : null;
    return (
      <RowView
        key={key}
        row={row}
        columns={columns}
        editing={editing}
        confirming={confirming}
        canEdit={canEdit}
        colSpan={columns.length + 1}
        fallbackFocusKey={fallbackFocusKey}
        deleteLabel={deleteLabel}
        expanded={expanded}
        rowActions={rowActions}
        onOpenEdit={(colKey) => openEdit(row, colKey)}
        onSetDraft={setDraft}
        onSave={save}
        onCancel={cancel}
        onAskDelete={() => {
          if (dirty) return;
          setEdit(null);
          setConfirm({ key, deleting: false, error: null });
        }}
        onConfirmDelete={async () => {
          setConfirm((cur) =>
            cur && cur.key === key ? { ...cur, deleting: true, error: null } : cur,
          );
          try {
            await onDelete(key);
            setConfirm((cur) => (cur?.key === key ? null : cur));
          } catch (err) {
            // Wie beim Speichern: Rueckfrage bleibt offen, Fehler wird sichtbar
            // (dieselbe Darstellung wie edit.error), statt die Zeile stillschweigend
            // zurueckzusetzen.
            const mapped = mapError?.(err) ?? null;
            setConfirm((cur) =>
              cur && cur.key === key
                ? { ...cur, deleting: false, error: mapped?.message ?? t('catalog.errors.generic') }
                : cur,
            );
          }
        }}
        onAbortDelete={() => setConfirm((cur) => (cur?.key === key ? null : cur))}
      />
    );
  };

  // Gruppen in Zeilenreihenfolge (erstes Auftreten gewinnt) — der Tab hat schon sortiert.
  const groups: { key: string; label: string; rows: TRow[] }[] = [];
  if (groupBy) {
    const indexByKey = new Map<string, number>();
    for (const row of rows) {
      const g = groupBy(row);
      const at = indexByKey.get(g.key);
      if (at === undefined) {
        indexByKey.set(g.key, groups.length);
        groups.push({ key: g.key, label: g.label, rows: [row] });
      } else {
        groups[at]!.rows.push(row);
      }
    }
  }

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={openCreate}
            disabled={dirty}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10 disabled:opacity-40"
          >
            + {addLabel}
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-card border border-[var(--border)] bg-bg-surface">
        <table
          className="w-full table-fixed border-collapse text-sm"
          style={{ minWidth: computeTableMinWidth(columns) }}
        >
          <thead>
            <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
              {columns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left" style={{ width: col.width }}>
                  {col.header}
                </th>
              ))}
              <th className="w-44 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {groupBy
              ? groups.map((group) => {
                  const collapsed = collapsedGroups.has(group.key);
                  return (
                    <Fragment key={group.key}>
                      <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)]">
                        <td colSpan={columns.length + 1} className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={!collapsed}
                            className="flex items-center gap-2 text-left text-xs font-bold text-text-primary"
                          >
                            <span className="text-text-muted">{collapsed ? '▸' : '▾'}</span>
                            <span>{group.label}</span>
                            {/* Die Anzahl steht an der Gruppe, nicht nur in der Fußzeile: sie ist
                                die Antwort auf „wie viel hängt an diesem Stream" — und bleibt
                                lesbar, auch wenn die Gruppe zugeklappt ist. */}
                            <span className="font-normal text-text-muted">
                              {t('catalog.group.count', { count: group.rows.length })}
                            </span>
                          </button>
                        </td>
                      </tr>
                      {!collapsed && group.rows.map(renderRow)}
                    </Fragment>
                  );
                })
              : rows.map(renderRow)}

            {edit?.key === null && (
              <EditRow
                columns={columns}
                edit={edit}
                colSpan={columns.length + 1}
                onSetDraft={setDraft}
                onSave={save}
                onCancel={cancel}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowView<TRow, TDraft>({
  row,
  columns,
  editing,
  confirming,
  canEdit,
  colSpan,
  fallbackFocusKey,
  deleteLabel,
  expanded,
  rowActions,
  onOpenEdit,
  onSetDraft,
  onSave,
  onCancel,
  onAskDelete,
  onConfirmDelete,
  onAbortDelete,
}: {
  row: TRow;
  columns: CatalogColumn<TRow, TDraft>[];
  editing: EditState<TDraft> | null;
  confirming: ConfirmState | null;
  canEdit: boolean;
  colSpan: number;
  fallbackFocusKey: string | null;
  deleteLabel: (row: TRow) => string;
  expanded?: ((row: TRow) => ReactNode) | undefined;
  rowActions?: ((row: TRow) => ReactNode) | undefined;
  onOpenEdit: (columnKey: string) => void;
  onSetDraft: (draft: TDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  onAskDelete: () => void;
  onConfirmDelete: () => void;
  onAbortDelete: () => void;
}) {
  const { t } = useTranslation();

  if (editing) {
    return (
      <EditRow
        columns={columns}
        edit={editing}
        colSpan={colSpan}
        row={row}
        onSetDraft={onSetDraft}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  if (confirming) {
    return (
      <>
        <tr className="border-b border-[var(--border)] last:border-0">
          <td colSpan={colSpan} className="px-3 py-2">
            <div className="flex items-center gap-3">
              <span className="text-sm text-text-body">
                {t('catalog.confirm.deleteNamed', { name: deleteLabel(row) })}
              </span>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={confirming.deleting}
                className="rounded-btn bg-status-fail px-3 py-1 text-xs text-white disabled:opacity-60"
              >
                {t('catalog.actions.delete')}
              </button>
              <button
                type="button"
                onClick={onAbortDelete}
                className="px-2 py-1 text-xs text-text-muted"
              >
                {t('catalog.actions.cancel')}
              </button>
            </div>
          </td>
        </tr>
        {confirming.error && (
          <tr>
            <td colSpan={colSpan} className="px-3 pb-2 text-xs text-status-fail">
              {confirming.error}
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <>
      <tr className="group border-b border-[var(--border)] last:border-0">
        {columns.map((col) => (
          <td
            key={col.key}
            // break-words: langer, umbruchloser Zellinhalt (z. B. eine SAP-Note-URL in der
            // Beschreibung) ragte unter `table-fixed` sonst über die Zellgrenze in die
            // Nachbarspalte — die Spalten überlagerten sich sichtbar.
            className="px-3 py-2 align-top text-text-body break-words"
            onDoubleClick={() => onOpenEdit(col.key)}
          >
            {col.render(row)}
          </td>
        ))}
        <td className="px-3 py-2 text-right">
          {canEdit && (
            <span className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
              {rowActions?.(row)}{' '}
              <button
                type="button"
                onClick={() => onOpenEdit(fallbackFocusKey ?? '')}
                className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-text-body"
              >
                {t('catalog.actions.edit')}
              </button>{' '}
              <button
                type="button"
                onClick={onAskDelete}
                className="rounded-btn px-2 py-1 text-xs text-text-muted hover:text-status-fail"
              >
                {t('catalog.actions.delete')}
              </button>
            </span>
          )}
        </td>
      </tr>
      {expanded && <>{expanded(row)}</>}
    </>
  );
}

function EditRow<TRow, TDraft>({
  columns,
  edit,
  colSpan,
  row,
  onSetDraft,
  onSave,
  onCancel,
}: {
  columns: CatalogColumn<TRow, TDraft>[];
  edit: EditState<TDraft>;
  colSpan: number;
  /** Nur bei einer bestehenden Zeile gesetzt (Anlegen hat keine Zeile). Feste Felder (kein
   *  `editor`) zeigen damit ihren echten Wert (`col.render(row)`) statt eines generischen
   *  Platzhaltertexts — sonst wuerde z. B. eine ID beim Aufklappen zum Bearbeiten verschwinden
   *  (#52-Task-8-Review: erste tatsaechliche Nutzung eines festen Felds mit vorhandenem Wert). */
  row?: TRow;
  onSetDraft: (draft: TDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  // Enter speichert, Escape verwirft — beide mit stopPropagation, damit sie nicht an einen
  // globalen Tastatur-Handler durchschlagen (Lehre aus Paket B: der Playlist-Handler buchte
  // sonst einen Testlauf, waehrend ein Overlay offen war). In einem mehrzeiligen Feld macht
  // Enter einen Zeilenumbruch; dort speichert Strg+Enter.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const multiline = (e.target as HTMLElement).tagName === 'TEXTAREA';
    if (e.key === 'Enter' && (!multiline || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      onSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <>
      <tr
        className="border-b border-[var(--border)] bg-brand-accent/5 last:border-0"
        onKeyDown={onKeyDown}
      >
        {columns.map((col) => (
          <td key={col.key} className="px-3 py-2 align-top">
            {col.editor ? (
              <EditorField
                editor={col.editor}
                label={col.header}
                // Abnahme-Befund 2 (#112): In der ANLEGEZEILE steht neben den Eingaben kein
                // Bestandswert — die Zeile besteht aus leeren Kaesten und den "—"-Platzhaltern
                // der festen Felder. Welcher Kasten welcher ist, sagte nur die Kopfzeile, die am
                // unteren Ende einer langen Tabelle laengst aus dem Bild gescrollt ist; gemeldet
                // als „ich kann nur den deutschen Text eingeben". Beim BEARBEITEN entsteht das
                // Problem nicht, dort steht der bisherige Wert im Feld.
                placeholder={row === undefined ? col.header : undefined}
                draft={edit.draft}
                onChange={onSetDraft}
                autoFocus={edit.focusColumn === col.key}
                invalid={edit.error?.columnKey === col.key}
              />
            ) : row !== undefined ? (
              <span className="text-text-muted">{col.render(row)}</span>
            ) : (
              <span className="text-text-muted">{t('catalog.fixedField')}</span>
            )}
          </td>
        ))}
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            onClick={onSave}
            disabled={edit.saving}
            className="rounded-btn bg-brand-accent px-3 py-1 text-xs text-white disabled:opacity-60"
          >
            {t('catalog.actions.save')}
          </button>{' '}
          <button type="button" onClick={onCancel} className="px-2 py-1 text-xs text-text-muted">
            {t('catalog.actions.discard')}
          </button>
        </td>
      </tr>
      {edit.error && (
        <tr>
          <td colSpan={colSpan} className="px-3 pb-2 text-xs text-status-fail">
            {edit.error.message}
          </td>
        </tr>
      )}
    </>
  );
}

// Laesst ein textarea-Feld mit seinem Inhalt wachsen (Spec Kapitel 2: "Es gibt keinen
// Ausweichdialog"). Ein Wrapper statt zwei Kopien der drei Zeilen, damit Mount und Tippen
// garantiert denselben Weg nehmen.
function resizeTextarea(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function EditorField<TDraft>({
  editor,
  label,
  placeholder,
  draft,
  onChange,
  autoFocus,
  invalid,
}: {
  editor: CatalogEditor<TDraft>;
  label: string;
  /** Nur in der Anlegezeile gesetzt (Spaltenkopf als Hinweis im leeren Feld), sonst undefined. */
  placeholder: string | undefined;
  draft: TDraft;
  onChange: (draft: TDraft) => void;
  autoFocus: boolean;
  invalid: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // Beim ersten Rendern sizen, nicht erst beim ersten Tastendruck — sonst ist eine Zeile mit
  // langem Bestandstext beim Oeffnen abgeschnitten (Finding aus Task-1-Review).
  useEffect(() => {
    if (editor.kind === 'textarea') resizeTextarea(ref.current as HTMLTextAreaElement);
  }, [editor.kind]);

  const cls = `w-full rounded-btn border bg-bg-surface px-1.5 py-1 text-sm text-text-body ${
    invalid ? 'border-status-fail' : 'border-[var(--border)]'
  }`;

  if (editor.kind === 'checkbox') {
    return (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        type="checkbox"
        aria-label={label}
        checked={editor.get(draft)}
        onChange={(e) => onChange(editor.set(draft, e.target.checked))}
        className="h-4 w-4"
      />
    );
  }

  if (editor.kind === 'combobox') {
    return (
      <ComboboxField
        editor={editor}
        label={label}
        draft={draft}
        onChange={onChange}
        autoFocus={autoFocus}
        cls={cls}
      />
    );
  }

  if (editor.kind === 'select') {
    return (
      <select
        ref={ref as React.Ref<HTMLSelectElement>}
        aria-label={label}
        value={editor.get(draft) ?? ''}
        onChange={(e) => onChange(editor.set(draft, e.target.value || null))}
        className={cls}
      >
        {editor.allowEmpty && (
          <option value="">{editor.emptyLabel ?? t('catalog.actions.none')}</option>
        )}
        {editor.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (editor.kind === 'textarea') {
    return (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        aria-label={label}
        placeholder={placeholder}
        value={editor.get(draft)}
        rows={1}
        onChange={(e) => onChange(editor.set(draft, e.target.value))}
        // Die Zeile waechst mit dem Text, statt ihn abzuschneiden oder in einen Dialog
        // auszuweichen (Spec Kapitel 2).
        onInput={(e) => resizeTextarea(e.currentTarget)}
        className={`${cls} resize-none overflow-hidden`}
      />
    );
  }

  return (
    <input
      ref={ref as React.Ref<HTMLInputElement>}
      type="text"
      aria-label={label}
      placeholder={placeholder}
      value={editor.get(draft)}
      onChange={(e) => onChange(editor.set(draft, e.target.value))}
      className={cls}
    />
  );
}

// #47/#82 (Task 4): Braucht eigenen lokalen Zustand fuer den Tippfilter, waehrend EditorField
// sonst zustandslos zwischen den Editor-Arten umschaltet. `set`/`setNew` schreiben ausschliesslich
// in den Draft; Enter/Escape fasst diese Komponente nicht an — die bleiben beim umschliessenden
// EditRow-onKeyDown. Das Ein-/Ausgabegeruest (Input, Dropdown, a11y-kritische Eintraege) kommt
// seit Task 4 aus components/combobox.tsx — AddMember (e2e-tab.tsx, #82) nutzt dieselben Bausteine
// fuer die Testfall-Suche.
function ComboboxField<TDraft>({
  editor,
  label,
  draft,
  onChange,
  autoFocus,
  cls,
}: {
  editor: Extract<CatalogEditor<TDraft>, { kind: 'combobox' }>;
  label: string;
  draft: TDraft;
  onChange: (draft: TDraft) => void;
  autoFocus: boolean;
  cls: string;
}) {
  const { t } = useTranslation();
  const selected = editor.options.find((o) => o.value === editor.get(draft)) ?? null;
  // Nur beim Mount aus dem Draft abgeleitet (Auswahl-Label oder laufender Anlage-Freitext) —
  // die Zeile wird bei jedem Editieren-Start frisch gemountet (RowView/EditRow sind ueber den
  // Zeilenschluessel gekeyed), ein Re-Sync bei jedem Draft-Wechsel wuerde sonst den Tippfilter
  // waehrend des Tippens ueberschreiben.
  const [text, setText] = useState(selected?.label ?? editor.getNew(draft) ?? '');

  const trimmed = text.trim();
  const exact = editor.options.find((o) => o.label.toLowerCase() === trimmed.toLowerCase()) ?? null;
  const filtered =
    trimmed === ''
      ? editor.options
      : editor.options.filter((o) => o.label.toLowerCase().includes(trimmed.toLowerCase()));
  // "+ anlegen" nur bei getipptem, nicht schon exakt vorhandenem Namen — sonst bewuerbe man das
  // Anlegen eines Duplikats der gerade ausgewaehlten Datenart.
  const showCreate = editor.canCreate && trimmed !== '' && !exact;
  const match = showCreate ? editor.similar(trimmed) : null;

  // Review-Finding (#47 Task 7): der Draft muss WYSIWYG dem Eingabefeld folgen, genau wie bei
  // `text`/`select`. Vorher schrieb nur ein Klick auf eine Dropdown-Option/den Anlegen-Eintrag
  // in den Draft — Enter oder der Speichern-Button uebernahmen dann stillschweigend den alten
  // Draft-Stand, ein getippter neuer Name ging beim Speichern per Enter verloren. Deshalb hier
  // bei jedem Tastendruck denselben Abgleich fahren wie beim Anzeigen des Dropdowns (exakter
  // Treffer -> Auswahl, sonst bei canCreate -> Anlage-Freitext, sonst keine Auswahl).
  const commitTyped = (value: string) => {
    const trimmedValue = value.trim();
    const exactMatch = editor.options.find(
      (o) => o.label.toLowerCase() === trimmedValue.toLowerCase(),
    );
    if (exactMatch) {
      onChange(editor.set(draft, exactMatch.value));
    } else if (editor.canCreate && trimmedValue !== '') {
      onChange(editor.setNew(draft, trimmedValue));
    } else {
      onChange(editor.set(draft, null));
    }
  };

  return (
    <Combobox
      label={label}
      value={text}
      onChange={(value) => {
        setText(value);
        commitTyped(value);
      }}
      autoFocus={autoFocus}
      className={cls}
    >
      {({ open, close, listboxId }) =>
        open && (filtered.length > 0 || showCreate) ? (
          <ComboboxList id={listboxId}>
            {filtered.map((o) => (
              <ComboboxOption
                key={o.value}
                onSelect={() => {
                  setText(o.label);
                  close();
                  onChange(editor.set(draft, o.value));
                }}
              >
                {o.label}
              </ComboboxOption>
            ))}
            {/* Die Warnung steht VOR dem Anlegen-Eintrag und ist farblich betont: sie soll den
                Blick erreichen, bevor jemand das Duplikat anlegt. Stand sie darunter in grauer
                Kleinschrift, las sie niemand — der Anlegen-Eintrag in Akzentfarbe zog den Blick
                zuerst, und Enter legt direkt an (gemeldeter Befund zu "Werk"/"Wrek"). */}
            {match && (
              <li className="flex items-center justify-between gap-2 border-b border-[var(--border)] bg-status-warn/10 px-2 py-1.5 text-xs text-status-warn">
                <span>⚠ {t('catalog.dataKinds.similarHint', { name: match.label })}</span>
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setText(match.label);
                    close();
                    onChange(editor.set(draft, match.value));
                  }}
                  className="shrink-0 font-medium underline"
                >
                  {t('catalog.dataKinds.adopt')}
                </button>
              </li>
            )}
            {showCreate && (
              <ComboboxOption
                onSelect={() => {
                  close();
                  onChange(editor.setNew(draft, trimmed));
                }}
                className="block w-full px-2 py-1 text-left text-brand-accent hover:bg-brand-accent/10"
              >
                {t('catalog.dataKinds.create', { name: trimmed })}
              </ComboboxOption>
            )}
          </ComboboxList>
        ) : null
      }
    </Combobox>
  );
}
